import { LoaderCircle } from 'lucide-react';
import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { CanvasCapture } from '@repo/contracts';

import { trackEvent } from '../analytics/posthog';
import { CreditDot } from '../credits/credit-chip';
import { useCredits } from '../credits/use-credits';
import { FixListCard } from '../fix-list/room-fix-list';
import {
  ArrowLeftIcon,
  CameraIcon,
  FitIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from '../lib/icons';
import { Skeleton } from '../lib/skeleton';
import {
  ANALYSIS_GAP,
  ANALYSIS_WIDTH,
  AnalysisCard,
} from '../research-room/analysis-card';
import {
  type Spotlight,
  useResearchRoomStore,
} from '../store/research-room-store';
import { HeatmapOverlay } from './heatmap-overlay';
import { SpotEdgeHint } from './spot-edge-hint';
import { SpotlightOverlay } from './spotlight-overlay';
import { hostOf } from './target-url';
import {
  type Box,
  FLIGHT_MS,
  OPENING_ZOOM_MAX,
  useCanvasViewport,
} from './use-canvas-viewport';

/**
 * The board: every capture laid out on one infinite, pannable surface.
 *
 * A mode of the browse screen rather than its own route, deliberately — the
 * worker session has to survive the switch (leaving the route closes the socket
 * and the browser with it), and the protocol is built for exactly this: the
 * client tells the worker `visible: false` when it moves to the board, so an
 * instance stops encoding frames nobody is looking at. Users spend most of
 * their time here, so that is the common case, not an edge case.
 *
 * Cards live in *board* coordinates at their true capture size, so a desktop
 * capture and a phone capture sit side by side at honest relative scale — the
 * comparison the board exists for. Zoom is a property of the view, never of the
 * content.
 */

/** How far a pointer must travel before a press becomes a drag, in screen px. */
const DRAG_THRESHOLD_PX = 4;
/** How close to the board's edge a drag starts carrying the board along,
 *  and how fast at the very edge, in screen pixels a frame. */
const EDGE_PAN_PX = 40;
const EDGE_PAN_SPEED = 18;

/**
 * The bar above a card — a white strip with the screen's name and size —
 * and the gap under it, in board pixels: the design's 48px bar with 12px
 * under it, for a 732-wide screen, scaled to a 1280-wide one.
 */
const LABEL_HEIGHT = 104;
const LABEL_GAP = 20;

/**
 * How long after mount the board may still re-frame itself.
 *
 * Long enough to cover the layout arriving in pieces, and short enough that a
 * resize the *user* causes later leaves the board exactly where it was.
 * Everything after this window is somebody's deliberate act, and the board
 * should not answer it by moving.
 */
const SETTLE_MS = 1_500;

/** Arriving from a walk: how long the whole saved page stays in view before
 *  the board moves in on its top, and how close it goes. The whole page at
 *  once is too small to read; its top is where reading starts. */
const READ_TOP_AFTER_MS = 700;
const READ_TOP_ZOOM = 0.5;
/** Room above the page's top edge, in screen pixels, clear of the top bar. */
const READ_TOP_GAP = 72;

/**
 * Coming back to a room: its first screen and fix list, as wide as the clear
 * part of the board allows up to `ROOM_ZOOM_MAX`, with their top edge
 * `ROOM_TOP_SHARE` of the way down — where reading starts, not the whole
 * page shrunk to fit its height.
 */
const ROOM_TOP_SHARE = 0.25;
const ROOM_ZOOM_MAX = 0.8;

/** The floating pills' distance from the board's edge, and from the room. */
const CHROME_INSET = 12;
const CHROME_GAP = 10;

/** How long a hovered line's light takes to go once the pointer leaves. */
const PREVIEW_FADE_MS = 220;

/** How hard a pinch bites. Tuned so an ordinary trackpad pinch lands near the
 *  ±12% per event the board used to step by, without being fixed to it. */
const ZOOM_SENSITIVITY = 0.01;

/** One wheel event may not zoom further than this, whatever the device claims.
 *  A mouse wheel reports deltas an order of magnitude past a trackpad's, and
 *  without a ceiling one notch crosses most of the zoom range. */
const MAX_WHEEL_DELTA = 40;

/** What a `deltaMode` of "lines" is worth in pixels. */
const LINE_HEIGHT_PX = 16;

interface Placement {
  x: number;
  y: number;
}

/**
 * A screen on its way onto the board — the live browser a walk is driving,
 * then the page it stitched, before either is a stored capture.
 *
 * Drawn where the first capture of a session lands (0,0), at the size it will
 * be stored at, so when the stored capture replaces it on the session's own
 * board nothing moves.
 */
export interface PendingCard {
  /** Changing it re-frames the board, as a flight. */
  key: string;
  width: number;
  height: number;
  label: string;
  meta: string;
  /** Still being captured: a spinner in its bar. */
  busy?: boolean;
  /** In board pixels — the worker's own viewport space. */
  content: React.ReactNode;
  /** How far in the board may frame it. Defaults to the opening fit's. */
  ceiling?: number;
}

type Framed = Pick<CanvasCapture, 'height' | 'width' | 'x' | 'y'>;

function contentBounds(captures: Framed[]): Box | null {
  if (captures.length === 0) return null;

  const left = Math.min(...captures.map((c) => c.x));
  const top = Math.min(...captures.map((c) => c.y - LABEL_HEIGHT));
  const right = Math.max(...captures.map((c) => c.x + c.width));
  const bottom = Math.max(...captures.map((c) => c.y + c.height));

  return { height: bottom - top, width: right - left, x: left, y: top };
}

/** Whether two spotlights light the same part of the same screen. */
function samePlace(a: Spotlight, b: Spotlight): boolean {
  return (
    a.captureId === b.captureId &&
    a.focus.x === b.focus.x &&
    a.focus.y === b.focus.y &&
    a.focus.width === b.focus.width &&
    a.focus.height === b.focus.height
  );
}

export function CanvasBoard({
  captures,
  onExit,
  onMove,
  onSelect,
  onClearSpotlight,
  selectedId,
  enter = true,
  readTop = false,
  exitLabel,
  analysis = false,
  fixList = false,
  heatmap = false,
  loading = false,
  notice,
  panel,
  pending = null,
  spotlight = null,
  status,
  title = 'Canvas',
  titlePending = false,
  topRight,
}: {
  captures: CanvasCapture[];
  /** Fade in on mount. Off when arriving from a screen that already showed
   *  this board, so the handover has no seam. */
  enter?: boolean;
  /** Once the board has opened on the whole page, move in on the top of its
   *  first screen at a readable zoom — for the board a walk just saved. */
  readTop?: boolean;
  /** A word about what the board is doing, over it in screen space — centred
   *  in the part the panel leaves clear. */
  notice?: React.ReactNode;
  /**
   * The side panel — the research room, or the walk's progress — floating on
   * the board's right edge rather than beside it, so the bar above spans the
   * whole screen and the board runs underneath. The board frames what it
   * shows in the part the panel leaves clear.
   */
  panel?: React.ReactNode;
  /** A screen still on its way — see PendingCard. Shown on an empty board. */
  pending?: PendingCard | null;
  /** A word beside the title, when the page has one to say. */
  status?: React.ReactNode;
  /** Shown only when there is an `onExit` to give it. */
  exitLabel?: string;
  title?: string;
  /** App-level controls for the bar — the fix list lives here. */
  topRight?: React.ReactNode;
  /**
   * Leaving the board for somewhere else.
   *
   * Optional, because it is not always somewhere else: on `/browse` exiting the
   * board and capturing another both mean "show me the live page", so the two
   * buttons sat next to each other calling the same function under different
   * names. A screen with nowhere distinct to go omits it.
   */
  onExit?: () => void;
  onMove: (id: string, x: number, y: number) => void;
  onSelect: (captureId: string | null) => void;
  /** Show where the research room's attention landed on each capture. */
  heatmap?: boolean;
  /** The screens are still loading: their shapes, not "nothing here yet". */
  loading?: boolean;
  /** The title is still loading: its shape, not a placeholder word. */
  titlePending?: boolean;
  /** Show each room's write-up beside its screen, and answer the room's
   *  "take me to it" and "take me back". Kept, but no page shows it now —
   *  the fix list took its place. */
  analysis?: boolean;
  /** Show each room's fix list beside its screen — framed in from the
   *  start, so it is there as soon as the room is — and answer the room's
   *  "take me to it" and "take me back". */
  fixList?: boolean;
  /** The part of a screen the research room is talking about, lit up. */
  spotlight?: Spotlight | null;
  onClearSpotlight?: () => void;
  /**
   * The selected capture, owned by the parent.
   *
   * Controlled rather than internal: the parent selects the first capture when
   * the board opens, and a board holding its own copy would immediately report
   * null back over it.
   */
  selectedId: string | null;
}) {
  const credits = useCredits();
  const surfaceRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  /**
   * The part of the surface nothing floats over — what framing is for.
   *
   * Measured rather than assumed: the panel's width is its own business, and
   * the room can be closed, which gives the board its whole width back.
   */
  const frameSize = useCallback(() => {
    const element = surfaceRef.current;
    if (!element) return null;
    const rect = element.getBoundingClientRect();
    const covering = panelRef.current?.getBoundingClientRect();
    // The room's rail of faces hangs off the panel's left edge, over the
    // board — it covers the board just as much as the panel does.
    const rail = panelRef.current
      ?.querySelector('[data-roster]')
      ?.getBoundingClientRect();
    const left = Math.min(covering?.left ?? Infinity, rail?.left ?? Infinity);
    const clear = Number.isFinite(left)
      ? Math.max(0, left - rect.left)
      : rect.width;
    return { height: rect.height, width: clear };
  }, []);
  const {
    cancelFlight,
    fitTo,
    fitTop,
    flyTo,
    flyToFit,
    flyToViewport,
    panBy,
    toBoard,
    viewport,
    zoomAt,
  } = useCanvasViewport();
  const selected = selectedId;
  const setSelected = onSelect;
  const [panning, setPanning] = useState(false);

  /**
   * Where the card being dragged is right now. Server positions are the source
   * of truth, but a drag must not wait on a round trip per pointer move — so
   * the live position is local and only the final spot is sent (`onMove` on
   * pointerup, which the mutation applies optimistically anyway).
   */
  const [dragging, setDragging] = useState<(Placement & { id: string }) | null>(
    null,
  );

  const positionOf = useCallback(
    (capture: CanvasCapture): Placement =>
      dragging?.id === capture.id
        ? { x: dragging.x, y: dragging.y }
        : { x: capture.x, y: capture.y },
    [dragging],
  );

  const shown = pending && captures.length === 0 ? pending : null;
  const pendingWidth = shown?.width;
  const pendingHeight = shown?.height;
  // Where the notice, or the loading frames, centre: the clear part of the
  // surface, measured once there is something to centre.
  const [clearWidth, setClearWidth] = useState<number | null>(null);
  const hasNotice = Boolean(notice);
  useLayoutEffect(() => {
    if (!hasNotice && !loading) return;
    setClearWidth(frameSize()?.width ?? null);
  }, [frameSize, hasNotice, loading]);

  // The right-hand pills sit just left of the room, which now runs the full
  // height; with no room, at the board's own edge.
  const [chromeRight, setChromeRight] = useState(CHROME_INSET);
  const hasPanel = Boolean(panel);
  useLayoutEffect(() => {
    const surface = surfaceRef.current;
    const covering = panelRef.current;
    if (!surface || !covering || !hasPanel) {
      setChromeRight(CHROME_INSET);
      return;
    }
    const measure = () => {
      const right = surface.getBoundingClientRect().right;
      const left = covering.getBoundingClientRect().left;
      setChromeRight(Math.max(CHROME_INSET, right - left + CHROME_GAP));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(surface);
    observer.observe(covering);
    return () => {
      observer.disconnect();
    };
  }, [hasPanel]);

  const bounds = useMemo(() => {
    const pendingFrame =
      pendingWidth !== undefined && pendingHeight !== undefined;
    const box = contentBounds(
      pendingFrame
        ? [{ height: pendingHeight, width: pendingWidth, x: 0, y: 0 }]
        : captures,
    );
    // The fix list sits right of its screen: framing the screens alone left
    // the rightmost one's list off the edge until somebody went looking.
    return box && fixList && !pendingFrame
      ? { ...box, width: box.width + ANALYSIS_GAP + ANALYSIS_WIDTH }
      : box;
  }, [captures, fixList, pendingHeight, pendingWidth]);
  const openingCeiling = shown?.ceiling ?? OPENING_ZOOM_MAX;
  const arriving = shown !== null;

  const fit = useCallback(
    (ceiling = 1) => {
      const size = frameSize();
      if (!size) return;
      fitTo(bounds, size, ceiling);
    },
    [bounds, fitTo, frameSize],
  );

  // What a room opens on: its first screen, with its label, and its fix list
  // wherever that was moved to. Not every screen — framing them all is what
  // shrank each one. A board still waiting on its page, or arriving from a
  // walk (which shows the whole page first, then reads its top), keeps the
  // whole-board fit.
  const fixCardPositions = useResearchRoomStore((s) => s.fixCardPositions);
  const roomFrame = useMemo((): Box | null => {
    const first = captures[0];
    if (shown || readTop || !first) return null;
    let left = first.x;
    let right = first.x + first.width;
    // The page's own top, whatever sits above it: a fix list dragged
    // higher would otherwise push the page down the screen.
    const top = first.y - LABEL_HEIGHT;
    const bottom = first.y + first.height;
    if (fixList) {
      const placed = fixCardPositions[first.id];
      const x = placed?.x ?? first.x + first.width + ANALYSIS_GAP;
      left = Math.min(left, x);
      right = Math.max(right, x + ANALYSIS_WIDTH);
    }
    return { height: bottom - top, width: right - left, x: left, y: top };
  }, [captures, fixCardPositions, fixList, readTop, shown]);

  const openingFit = useCallback(() => {
    const size = frameSize();
    if (!size) return;
    if (roomFrame) {
      fitTop(roomFrame, size, {
        ceiling: ROOM_ZOOM_MAX,
        topShare: ROOM_TOP_SHARE,
      });
      return;
    }
    fitTo(bounds, size, openingCeiling);
  }, [bounds, fitTo, fitTop, frameSize, openingCeiling, roomFrame]);

  // Frame the board on open. Landing on a hard-to-find corner of an infinite
  // surface is the classic way a canvas reads as broken.
  //
  // The surface can change width a beat after mount, after a one-shot fit
  // would have already measured it. A ResizeObserver re-fits on every width
  // change instead, so it catches whatever shares this row mounting or
  // resizing before the surface has settled — up until the user actually
  // touches the viewport, after which their own zoom/pan must never be
  // overwritten.
  const interactedRef = useRef(false);
  const settleUntilRef = useRef(0);
  const touched = useCallback(() => {
    interactedRef.current = true;
    cancelFlight();
    // Moving off the write-up by hand ends looking at it: "back" would
    // take them somewhere they have already left.
    const room = useResearchRoomStore.getState();
    if (room.viewingAnalysis) room.setViewingAnalysis(false);
  }, [cancelFlight]);
  useLayoutEffect(() => {
    const element = surfaceRef.current;
    if (!element || !bounds) return;
    settleUntilRef.current ||= Date.now() + SETTLE_MS;
    // Once now, before first paint: the observer's first callback lands a
    // frame later, and that frame would show the board unframed.
    if (!interactedRef.current && Date.now() <= settleUntilRef.current) {
      openingFit();
    }
    // jsdom (component tests) has no ResizeObserver; tests never resize.
    if (typeof ResizeObserver === 'undefined') return;
    const refit = () => {
      if (interactedRef.current) return;
      // Only while the layout is still settling. Re-fitting on *every* width
      // change meant toggling anything beside the board rescaled it and moved
      // the frame you were already looking at.
      if (Date.now() > settleUntilRef.current) return;
      openingFit();
    };
    const observer = new ResizeObserver(refit);
    observer.observe(element);
    // The room covers part of the board, and it arrives in pieces too: its
    // rail of faces mounts once the personas load, after the board has
    // already framed itself in the width the rail now hangs over.
    const panel = panelRef.current;
    if (panel) observer.observe(panel);
    const mounting =
      panel && typeof MutationObserver !== 'undefined'
        ? new MutationObserver(refit)
        : null;
    if (panel) mounting?.observe(panel, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      mounting?.disconnect();
    };
  }, [bounds, openingFit]);

  // Fresh from a walk: the whole saved page first — the frame the walk ended
  // on, so the handover has no seam — then in on its top, where reading
  // starts. Once, and not if the researcher has already taken the board.
  const readTopDoneRef = useRef(false);
  const firstCapture = captures[0];
  useEffect(() => {
    if (!readTop || readTopDoneRef.current || !firstCapture) return;
    readTopDoneRef.current = true;
    const page = firstCapture;
    const timer = setTimeout(() => {
      const size = frameSize();
      if (interactedRef.current || !size) return;
      interactedRef.current = true;
      flyToViewport(
        {
          x: size.width / 2 - (page.x + page.width / 2) * READ_TOP_ZOOM,
          y: READ_TOP_GAP - page.y * READ_TOP_ZOOM,
          zoom: READ_TOP_ZOOM,
        },
        size,
      );
    }, READ_TOP_AFTER_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [firstCapture, flyToViewport, frameSize, readTop]);

  // A pending screen that changes shape — the live browser becoming the
  // stitched page — takes the view with it, whatever the researcher did:
  // it is the moment they were waiting for.
  const pendingKey = shown?.key;
  const framedKeyRef = useRef(pendingKey);
  useEffect(() => {
    if (framedKeyRef.current === pendingKey) return;
    framedKeyRef.current = pendingKey;
    const size = frameSize();
    if (!pendingKey || !bounds || !size) return;
    interactedRef.current = true;
    flyToFit(bounds, size, openingCeiling);
  }, [bounds, flyToFit, frameSize, openingCeiling, pendingKey]);

  // Take the researcher to a part of the screen they asked to see. One the
  // conversation raised is lit where it is and never moves the board —
  // moving on every line was the board being taken from them; the edge hint
  // says where it is when it is out of view.
  //
  // The store outlives the board, so the last request made in another room
  // is still there when this one opens. Only ones made since count — a
  // stale one flew a fresh board off its opening frame.
  const spotKeyAtOpen = useRef(spotlight?.key);
  useEffect(() => {
    if (!spotlight || spotlight.auto) return;
    if (spotlight.key === spotKeyAtOpen.current) return;
    const capture = captures.find((c) => c.id === spotlight.captureId);
    const size = frameSize();
    if (!capture || !size) return;
    interactedRef.current = true;
    flyTo(
      {
        height: spotlight.focus.height,
        width: spotlight.focus.width,
        x: capture.x + spotlight.focus.x,
        y: capture.y + spotlight.focus.y,
      },
      size,
      // Close enough to read, far enough to keep what is around it.
      { maxZoom: 0.85, share: 0.5 },
    );
    // Keyed on the request, not the captures: a capture refetch must not
    // fly the board back to a spot it already showed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spotlight?.key]);

  // What is lit: a line being hovered, over whatever the room last raised.
  const previewSpot = useResearchRoomStore((s) => s.previewSpot);
  // An open persona profile hangs off the panel over the board, so the panel
  // has to rise above the board's floating pills (title, fix list) with it.
  const profileOpen = useResearchRoomStore((s) => s.profileId !== null);
  // A preview that just ended stays a moment, fading, rather than vanishing.
  const [fading, setFading] = useState<Spotlight | null>(null);
  const lastPreviewRef = useRef(previewSpot);
  useEffect(() => {
    const gone = lastPreviewRef.current;
    lastPreviewRef.current = previewSpot;
    if (previewSpot) {
      setFading(null);
      return;
    }
    if (!gone) return;
    setFading(gone);
    const timer = setTimeout(() => {
      setFading(null);
    }, PREVIEW_FADE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [previewSpot]);
  // A hovered line's ring is drawn beside what the room last raised, never
  // in its place: swapping them made the asked-for spot leave and arrive
  // again, label and all, every time a pointer crossed the thread. One on
  // the same place is already lit.
  const hovered = previewSpot ?? fading;
  const hoverSpot =
    hovered && !(spotlight && samePlace(hovered, spotlight)) ? hovered : null;
  const shownSpot = hoverSpot ?? spotlight;
  const previewing = hoverSpot !== null;

  // The room's toggle: to a screen's write-up, and back to wherever the
  // researcher was before it. Always goes — they asked.
  const analysisView = useResearchRoomStore((s) => s.analysisView);
  // Asked for before this board opened — in another room — is not asked.
  const analysisKeyAtOpen = useRef(analysisView?.key);
  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;
  const beforeAnalysisRef = useRef<typeof viewport | null>(null);
  useEffect(() => {
    if (!analysisView || analysisView.key === analysisKeyAtOpen.current) return;
    const size = frameSize();
    if (!size) return;
    if (analysisView.action === 'back') {
      const before = beforeAnalysisRef.current;
      beforeAnalysisRef.current = null;
      if (before) flyToViewport(before, size);
      return;
    }
    const capture = captures.find((c) => c.id === analysisView.captureId);
    if (!capture) return;
    const card = surfaceRef.current?.querySelector<HTMLElement>(
      `[data-fix-card="${capture.id}"], [data-analysis-card="${capture.id}"]`,
    );
    beforeAnalysisRef.current ??= viewportRef.current;
    interactedRef.current = true;
    // Wherever the fix list was moved to.
    const placed = useResearchRoomStore.getState().fixCardPositions[capture.id];
    flyTo(
      {
        // offsetHeight is in the card's own pixels — board pixels — whatever
        // the zoom.
        height: card?.offsetHeight ?? capture.height,
        width: ANALYSIS_WIDTH,
        x: placed?.x ?? capture.x + capture.width + ANALYSIS_GAP,
        y: placed?.y ?? capture.y,
      },
      size,
      { maxZoom: 1.4, share: 0.9 },
    );
    // Keyed on the request, like a spotlight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisView?.key]);

  // Escape clears a selection first, and leaves the board only when there is
  // nothing selected — and never on an Escape something else already took
  // (a popup, a menu, the persona card close on it and `preventDefault`). Written with `selected` as a dependency rather than
  // reading it inside a setSelected updater: an updater runs during render, so
  // calling onExit (a parent setState) from inside one warns about updating a
  // component while rendering another.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (
        event.target instanceof Element &&
        event.target.closest('[role="dialog"], [role="menu"], [role="listbox"]')
      ) {
        return;
      }
      if (selected) {
        setSelected(null);
        return;
      }
      // Not while a screen is still arriving: one keypress would throw away
      // a walk that is nearly done.
      if (arriving) return;
      onExit?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [arriving, onExit, selected, setSelected]);

  /**
   * Wheel and pinch, attached natively rather than through React's `onWheel`.
   *
   * React registers wheel handlers at the root as **passive**, so
   * `preventDefault()` inside `onWheel` does nothing at all (the browser warns
   * and carries on). A trackpad pinch arrives as a wheel event with `ctrlKey`
   * set, and left to itself the browser applies its own full-page zoom — so
   * pinching over the board scaled the entire app, header and panel and all,
   * while the canvas zoomed underneath it. The two happening at once is why it
   * read as the chrome zooming rather than as a missing preventDefault.
   *
   * The listener is on the surface, so the gesture is only claimed over the
   * board. Pinching over anything beside it still does whatever the browser
   * normally does.
   */
  useEffect(() => {
    const element = surfaceRef.current;
    if (!element) return;

    const onWheel = (event: WheelEvent) => {
      // The panel scrolls itself; only the board's own surface pans.
      if (
        event.target instanceof Node &&
        panelRef.current?.contains(event.target)
      ) {
        return;
      }
      event.preventDefault();
      touched();

      const rect = element.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      // Wheels report in pixels, lines or pages depending on the device; a
      // line is worth about a line of text and a page about a viewport.
      const scale =
        event.deltaMode === 1
          ? LINE_HEIGHT_PX
          : event.deltaMode === 2
            ? rect.height
            : 1;
      const deltaX = event.deltaX * scale;
      const deltaY = event.deltaY * scale;

      // Ctrl/⌘ + wheel is what a trackpad pinch sends, so it zooms; a plain
      // wheel pans, which is what every other canvas tool does.
      if (event.ctrlKey || event.metaKey) {
        // Proportional to how far the fingers actually moved, and exponential
        // so that zooming out then back in returns to where it started. The
        // old fixed ±12% per event threw the gesture's speed away, which is
        // what made a slow pinch lurch and a fast one crawl — and a mouse
        // wheel, whose deltas are an order of magnitude larger, jump.
        const step = Math.max(
          -MAX_WHEEL_DELTA,
          Math.min(MAX_WHEEL_DELTA, deltaY),
        );
        zoomAt(Math.exp(-step * ZOOM_SENSITIVITY), x, y);
        return;
      }
      panBy(-deltaX, -deltaY);
    };

    // passive: false is the whole point — without it preventDefault is ignored.
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      element.removeEventListener('wheel', onWheel);
    };
  }, [panBy, touched, zoomAt]);

  /**
   * Panning: a drag on the empty board, two-finger scroll (the wheel handler
   * above), Space + drag, or a middle-button drag. A drag that starts on a
   * screen moves the screen; Space or the middle button pans from anywhere.
   */
  const startPan = (event: ReactPointerEvent) => {
    touched();
    setPanning(true);
    if ('setPointerCapture' in event.currentTarget) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  // Space held is the hand tool — except while typing, where it is a space.
  const [spaceHeld, setSpaceHeld] = useState(false);
  useEffect(() => {
    const typing = (target: EventTarget | null) =>
      target instanceof HTMLElement &&
      (target.isContentEditable ||
        ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName));
    const down = (event: KeyboardEvent) => {
      if (event.code !== 'Space' || typing(event.target)) return;
      // No page scroll, and no "click" on a focused button.
      event.preventDefault();
      if (!event.repeat) setSpaceHeld(true);
    };
    const up = (event: KeyboardEvent) => {
      if (event.code === 'Space') setSpaceHeld(false);
    };
    const reset = () => {
      setSpaceHeld(false);
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', reset);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', reset);
    };
  }, []);

  const onPointerMove = (event: ReactPointerEvent) => {
    if (!panning) return;
    panBy(event.movementX, event.movementY);
  };

  const endPan = (event: ReactPointerEvent) => {
    if (!panning) return;
    setPanning(false);
    if (
      'hasPointerCapture' in event.currentTarget &&
      event.currentTarget.hasPointerCapture(event.pointerId)
    ) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  /** True from the moment a press becomes a drag until it lets go. */
  const [dragActive, setDragActive] = useState(false);
  /** The pointer during a drag, in screen pixels — for the edge pan. */
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{
    id: string;
    offsetX: number;
    offsetY: number;
    /** Where the pointer went down, in screen pixels. */
    fromX: number;
    fromY: number;
    /** False until the pointer has moved far enough to mean "drag". */
    moved: boolean;
  } | null>(null);

  const startCardDrag = (event: ReactPointerEvent, capture: CanvasCapture) => {
    if (event.button !== 0) return;
    // A control on or under the screen does its own thing; dragging from it
    // would swallow the click.
    if (
      event.target instanceof Element &&
      event.target.closest('a, button, input, textarea, select, [data-no-drag]')
    ) {
      return;
    }
    event.stopPropagation();
    pointerRef.current = { x: event.clientX, y: event.clientY };
    setSelected(capture.id);

    const rect = surfaceRef.current?.getBoundingClientRect();
    if (!rect) return;

    const point = toBoard(event.clientX - rect.left, event.clientY - rect.top);
    dragRef.current = {
      fromX: event.clientX,
      fromY: event.clientY,
      id: capture.id,
      moved: false,
      offsetX: point.x - capture.x,
      offsetY: point.y - capture.y,
    };
  };

  const onCardMove = (event: ReactPointerEvent) => {
    const drag = dragRef.current;
    const rect = surfaceRef.current?.getBoundingClientRect();
    if (!drag || !rect) return;

    // Nothing moves until the pointer has travelled past the threshold. Without
    // it, the tremor between pressing and releasing a click nudged the card, so
    // selecting a capture moved it a few pixels every time.
    pointerRef.current = { x: event.clientX, y: event.clientY };
    if (!drag.moved) {
      const travelled = Math.hypot(
        event.clientX - drag.fromX,
        event.clientY - drag.fromY,
      );
      if (travelled < DRAG_THRESHOLD_PX) return;
      drag.moved = true;
      setDragActive(true);
      // The screen's fix list stays where it is, not riding along: pinned
      // at its current spot before the screen leaves it.
      const room = useResearchRoomStore.getState();
      const moving = captures.find((capture) => capture.id === drag.id);
      if (moving && !room.fixCardPositions[drag.id]) {
        const at = positionOf(moving);
        room.moveFixCard(drag.id, {
          x: at.x + moving.width + ANALYSIS_GAP,
          y: at.y,
        });
        room.keepFixCardPositions();
      }
      // Taken only once it is a drag, so a plain click still lands where it
      // was pressed. Keeps the drag when the pointer outruns the screen; not
      // in every environment (jsdom), and a drag works without it.
      if ('setPointerCapture' in event.currentTarget) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
    }

    const point = toBoard(event.clientX - rect.left, event.clientY - rect.top);
    setDragging({
      id: drag.id,
      x: point.x - drag.offsetX,
      y: point.y - drag.offsetY,
    });
  };

  const endCardDrag = (event: ReactPointerEvent) => {
    const drag = dragRef.current;
    dragRef.current = null;
    setDragActive(false);
    if (
      'hasPointerCapture' in event.currentTarget &&
      event.currentTarget.hasPointerCapture(event.pointerId)
    ) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (!drag?.moved || !dragging) return;

    // Persist the resting position, not every frame of the drag. `onMove` is
    // called here and NOT inside the setDragging updater: an updater runs
    // during render, so a parent setState from inside one is the "cannot update
    // a component while rendering a different component" warning.
    //
    // `dragging` is deliberately NOT cleared here. The optimistic write behind
    // onMove lands a tick later, so dropping the local position now renders one
    // frame at the card's old coordinates — the flicker on release. The effect
    // below clears it once the row itself has caught up.
    onMove(dragging.id, dragging.x, dragging.y);
  };

  // Dragging a screen towards the board's edge carries the board along, so a
  // screen can go anywhere — not only as far as the pointer can reach.
  const edgeArmedRef = useRef(false);
  useEffect(() => {
    if (!dragActive) return;
    edgeArmedRef.current = false;
    let frame = 0;
    const tick = () => {
      frame = requestAnimationFrame(tick);
      const pointer = pointerRef.current;
      const rect = surfaceRef.current?.getBoundingClientRect();
      const clear = frameSize();
      if (!pointer || !rect || !clear) return;
      // Faster the closer to the edge — and no faster past it, over the
      // room or off the window.
      // Armed only once the pointer has been clear of every edge: a press
      // that starts near one — easy on a screen as tall as the board — must
      // move the screen, not set the board drifting.
      const inset = Math.min(
        pointer.x - rect.left,
        rect.left + clear.width - pointer.x,
        pointer.y - rect.top,
        rect.bottom - pointer.y,
      );
      if (!edgeArmedRef.current) {
        if (inset > EDGE_PAN_PX) edgeArmedRef.current = true;
        return;
      }
      const push = (inside: number) =>
        inside < EDGE_PAN_PX
          ? Math.ceil(
              ((EDGE_PAN_PX - Math.max(0, inside)) / EDGE_PAN_PX) *
                EDGE_PAN_SPEED,
            )
          : 0;
      const dx =
        push(pointer.x - rect.left) - push(rect.left + clear.width - pointer.x);
      const dy = push(pointer.y - rect.top) - push(rect.bottom - pointer.y);
      if (dx === 0 && dy === 0) return;
      const { zoom } = viewportRef.current;
      panBy(dx, dy);
      // The board moved under a still pointer: the screen keeps up with it.
      setDragging((current) =>
        current
          ? { ...current, x: current.x - dx / zoom, y: current.y - dy / zoom }
          : current,
      );
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [dragActive, frameSize, panBy]);

  // Hand back to the server position only once it matches where the card was
  // dropped, so the card never jumps backwards on the way there.
  useEffect(() => {
    if (!dragging) return;
    const row = captures.find((capture) => capture.id === dragging.id);
    if (!row) return;
    if (
      Math.round(row.x) === Math.round(dragging.x) &&
      Math.round(row.y) === Math.round(dragging.y)
    ) {
      setDragging(null);
    }
  }, [captures, dragging]);

  const zoomPercent = Math.round(viewport.zoom * 100);

  return (
    <div
      className={[
        'flex h-svh min-w-0 flex-1 flex-col overflow-hidden bg-warm',
        enter ? 'duration-200 animate-in fade-in' : '',
      ].join(' ')}
    >
      <div className="flex min-h-0 flex-1">
        <div
          className={[
            'relative min-h-0 flex-1 touch-none overflow-hidden overscroll-none select-none',
            // Figma's hand: while Space is held (or a pan is under way) the
            // whole board, screens included, is something to grab.
            panning
              ? 'cursor-grabbing [&_*]:!cursor-grabbing'
              : spaceHeld
                ? 'cursor-grab [&_*]:!cursor-grab'
                : 'cursor-grab',
          ].join(' ')}
          onPointerCancel={endPan}
          // Space-drag and middle-drag pan from anywhere, over a screen or
          // not — so they are caught on the way down, before a screen can
          // take the press as a move.
          onPointerDownCapture={(event) => {
            if (event.button === 1 || (event.button === 0 && spaceHeld)) {
              event.preventDefault();
              event.stopPropagation();
              startPan(event);
            }
          }}
          // A plain press on the empty board selects nothing and grabs the
          // board. A screen stops its own press, so dragging one still moves
          // the screen.
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            setSelected(null);
            // A control keeps its click: capturing the pointer for a pan would
            // retarget the pointerup to the board.
            if (
              event.target instanceof Element &&
              event.target.closest(
                'a, button, input, textarea, select, [data-no-drag]',
              )
            ) {
              return;
            }
            startPan(event);
          }}
          onPointerMove={onPointerMove}
          onPointerUp={endPan}
          // Never scrolls: the board moves by its viewport, not by scrolling.
          // But `overflow: hidden` only hides the scrollbars — the browser
          // still scrolls it to bring a focused field or a scrolled-to line
          // into view, and the whole board and room slid up with it.
          onScroll={(event) => {
            const surface = event.currentTarget;
            if (surface.scrollTop !== 0) surface.scrollTop = 0;
            if (surface.scrollLeft !== 0) surface.scrollLeft = 0;
          }}
          ref={surfaceRef}
        >
          {/* The dotted grid scrolls and scales with the board, which is what
            makes panning legible — a fixed grid reads as the content sliding
            under glass, and at speed it looks like nothing moved at all. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:radial-gradient(#1a1a1a_1px,transparent_1px)]"
            style={{
              backgroundPosition: `${String(viewport.x)}px ${String(viewport.y)}px`,
              backgroundSize: `${String(24 * viewport.zoom)}px ${String(24 * viewport.zoom)}px`,
            }}
          />

          <div
            className="absolute top-0 left-0 origin-top-left"
            style={{
              transform: `translate(${String(viewport.x)}px, ${String(viewport.y)}px) scale(${String(viewport.zoom)})`,
            }}
          >
            {shown ? (
              <div
                className="absolute top-0 left-0 transition-[height] ease-in-out motion-reduce:transition-none"
                data-testid="pending-card"
                // Grows in step with the flight that re-frames it.
                style={{
                  height: shown.height,
                  transitionDuration: `${String(FLIGHT_MS)}ms`,
                  width: shown.width,
                }}
              >
                <ScreenBar
                  busy={shown.busy}
                  label={shown.label}
                  meta={shown.meta}
                />
                {shown.content}
              </div>
            ) : null}

            {captures.map((capture, index) => {
              const placement = positionOf(capture);
              const number = index + 1;
              const isSelected = selected === capture.id;
              const isDragged = dragActive && dragging?.id === capture.id;

              return (
                <div
                  className={[
                    // The whole screen is the handle — its name, the
                    // picture, and anything lit over it — like a frame on a
                    // design canvas.
                    'group absolute touch-none',
                    // Says "this moves" before anyone tries.
                    isDragged ? 'cursor-grabbing' : 'cursor-grab',
                  ].join(' ')}
                  key={capture.id}
                  onLostPointerCapture={endCardDrag}
                  onPointerCancel={endCardDrag}
                  onPointerDown={(event) => {
                    startCardDrag(event, capture);
                  }}
                  onPointerMove={onCardMove}
                  onPointerUp={endCardDrag}
                  style={{
                    height: capture.height,
                    left: placement.x,
                    top: placement.y,
                    width: capture.width,
                    // Above every other screen while it moves.
                    zIndex: isDragged ? 10 : undefined,
                  }}
                >
                  {/* Label sits above the frame, like a Figma frame name — it is
                    chrome, not content, so it never covers the screenshot. */}
                  {/* The model's name for the screen once it has one — a
                      board of "Capture 3" cards is unreadable at a glance,
                      which is the entire point of a board. */}
                  <ScreenBar
                    label={capture.name ?? `Capture ${String(number)}`}
                    meta={`${capture.pageUrl ? `${hostOf(capture.pageUrl)} · ` : ''}${String(capture.width)}×${String(capture.height)}`}
                  />

                  <CaptureImage
                    alt={`Capture ${String(number)} of ${hostOf(capture.pageUrl)}`}
                    className={[
                      // The ring eases in: selecting the first screen on
                      // arrival otherwise flashes a lime frame onto a board
                      // that was calm a moment ago.
                      'block size-full rounded-thumb bg-surface object-cover object-top ring-1 transition-shadow duration-500 ease-out',
                      isDragged ? 'shadow-modal' : 'shadow-floating',
                      // Figma's hover outline: this is the thing a drag would move.
                      isSelected
                        ? 'ring-[3px] ring-highlight'
                        : 'ring-hairline-alt group-hover:ring-2 group-hover:ring-highlight/70',
                    ].join(' ')}
                    draggable={false}
                    src={capture.imageUrl}
                  />

                  {heatmap ? (
                    <HeatmapOverlay
                      captureId={capture.id}
                      sessionId={capture.sessionId}
                      zoom={viewport.zoom}
                    />
                  ) : null}

                  {analysis ? (
                    <AnalysisCard
                      captureId={capture.id}
                      projectId={capture.projectId}
                      zoom={viewport.zoom}
                    />
                  ) : null}

                  {spotlight?.captureId === capture.id ? (
                    <SpotlightOverlay
                      onClose={() => {
                        trackEvent('spotlight_closed');
                        onClearSpotlight?.();
                      }}
                      spotlight={spotlight}
                      zoom={viewport.zoom}
                    />
                  ) : null}
                  {hoverSpot?.captureId === capture.id ? (
                    <SpotlightOverlay
                      bare
                      leaving={hoverSpot === fading}
                      spotlight={hoverSpot}
                      zoom={viewport.zoom}
                    />
                  ) : null}
                </div>
              );
            })}

            {/* Each screen's fix list: its own object on the board, after
                the screens so it sits above them. */}
            {fixList
              ? captures.map((capture) => {
                  const at = positionOf(capture);
                  return (
                    <FixListCard
                      anchor={{
                        x: at.x + capture.width + ANALYSIS_GAP,
                        y: at.y,
                      }}
                      captureId={capture.id}
                      key={`fix-${capture.id}`}
                      page={{ name: capture.name, url: capture.pageUrl }}
                      projectId={capture.projectId}
                      zoom={viewport.zoom}
                    />
                  );
                })
              : null}
          </div>

          {(() => {
            // A place lit without moving the board, but out of view: say
            // where it is from the edge.
            if (!shownSpot?.auto || shownSpot === fading) return null;
            const capture = captures.find((c) => c.id === shownSpot.captureId);
            const size = frameSize();
            if (!capture || !size) return null;
            const at = positionOf(capture);
            const { focus } = shownSpot;
            const target = {
              x:
                viewport.x + (at.x + focus.x + focus.width / 2) * viewport.zoom,
              y:
                viewport.y +
                (at.y + focus.y + focus.height / 2) * viewport.zoom,
            };
            const inView =
              target.x >= 0 &&
              target.x <= size.width &&
              target.y >= 0 &&
              target.y <= size.height;
            if (inView) return null;
            return (
              <SpotEdgeHint
                frame={size}
                onShow={() => {
                  const { key: _key, ...spot } = shownSpot;
                  const room = useResearchRoomStore.getState();
                  room.previewSpotlight(null);
                  room.showSpotlight({ ...spot, auto: false });
                }}
                soft={previewing}
                spot={shownSpot}
                target={target}
              />
            );
          })()}

          {loading && captures.length === 0 && !shown ? (
            // The board's first screens on their way: frames in the shape of
            // a run, in the part the panel leaves clear.
            <div
              aria-busy="true"
              aria-label="Loading the screens"
              className="pointer-events-none absolute inset-y-0 left-0 flex items-center justify-center gap-10 px-10"
              role="status"
              style={{ width: clearWidth ?? '100%' }}
            >
              {[0, 1, 2].map((i) => (
                <div
                  className="hidden w-64 shrink-0 duration-500 animate-in fade-in slide-in-from-bottom-2 fill-mode-backwards first:block motion-reduce:animate-none sm:block"
                  key={i}
                  style={{ animationDelay: `${String(i * 120)}ms` }}
                >
                  <Skeleton className="mb-3 h-3.5 w-28 rounded-pill" />
                  <div className="rounded-thumb bg-surface p-3 shadow-floating ring-1 ring-hairline">
                    <Skeleton className="h-4 w-1/2 rounded-pill" />
                    <Skeleton className="mt-4 h-24 w-full rounded-button" />
                    <Skeleton className="mt-3 h-3 w-4/5 rounded-pill" />
                    <Skeleton className="mt-2 h-3 w-3/5 rounded-pill" />
                  </div>
                </div>
              ))}
            </div>
          ) : captures.length === 0 && !shown ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="max-w-[320px] text-center">
                <CameraIcon className="mx-auto size-7 text-text-muted" />
                <p className="mt-4 text-ui-lg text-text-secondary">
                  Nothing on the canvas yet.
                </p>
                <p className="mt-1 text-ui text-text-muted">
                  Screens from a walk land here.
                </p>
              </div>
            </div>
          ) : null}

          {/* stopPropagation, or the surface's pointer capture swallows the
            click: capturing the pointer on pointerdown retargets the following
            pointerup to the surface, so the button never sees a click at all. */}
          <div
            className={[
              'absolute bottom-5 flex items-center gap-2',
              // The panel owns the right edge, so the controls take the left.
              panel ? 'left-5' : 'right-5',
            ].join(' ')}
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="flex items-center rounded-pill border border-hairline bg-surface p-1 shadow-soft">
              <button
                aria-label="Zoom out"
                className="flex h-8 cursor-pointer items-center justify-center rounded-pill px-1.5 text-text-control transition hover:bg-surface-hover hover:text-text-primary"
                onClick={() => {
                  trackEvent('board_zoomed', {
                    action: 'out',
                    zoom: zoomPercent,
                  });
                  touched();
                  const size = frameSize();
                  zoomAt(
                    1 / 1.2,
                    (size?.width ?? 0) / 2,
                    (size?.height ?? 0) / 2,
                  );
                }}
                type="button"
              >
                <ZoomOutIcon className="size-4" />
              </button>
              <span className="text-center text-xs tabular-nums text-text-control">
                {zoomPercent}%
              </span>
              <button
                aria-label="Zoom in"
                className="flex h-8 cursor-pointer items-center justify-center rounded-pill px-1.5 text-text-control transition hover:bg-surface-hover hover:text-text-primary"
                onClick={() => {
                  trackEvent('board_zoomed', {
                    action: 'in',
                    zoom: zoomPercent,
                  });
                  touched();
                  const size = frameSize();
                  zoomAt(1.2, (size?.width ?? 0) / 2, (size?.height ?? 0) / 2);
                }}
                type="button"
              >
                <ZoomInIcon className="size-4" />
              </button>
              <span aria-hidden="true" className="mx-1 h-4 w-px bg-hairline" />
              <button
                aria-label="Fit to screen"
                className="flex size-8 cursor-pointer items-center justify-center rounded-pill text-text-control transition hover:bg-surface-hover hover:text-text-primary"
                onClick={() => {
                  trackEvent('board_zoomed', {
                    action: 'fit',
                    zoom: zoomPercent,
                  });
                  touched();
                  fit();
                }}
                type="button"
              >
                <FitIcon className="size-4" />
              </button>
            </div>

            {/* What is left to spend, with the board's other standing
                readouts — out of the composer, where it sat on every
                question. The warnings when it runs low stay there. Its own
                pill beside the zoom, not folded into it: a balance is not a
                control. */}
            {credits.loaded && Number.isFinite(credits.balance.totalCredits) ? (
              <span className="flex h-10 items-center gap-2 rounded-pill border border-hairline bg-surface px-4 text-ui whitespace-nowrap shadow-soft">
                <CreditDot />
                <span className="font-semibold text-text-primary tabular-nums">
                  {credits.balance.totalCredits.toLocaleString()}
                </span>
                <span className="text-text-secondary">
                  {credits.balance.totalCredits === 1 ? 'Credit' : 'Credits'}
                </span>
              </span>
            ) : null}
          </div>

          {/* The board's chrome, floating on it rather than a bar across the
              top: the board runs to the edge, and the room gets the full
              height. Pointer-down stops here so pressing a pill never pans. */}
          <div
            className="absolute top-3 left-3 z-20 flex max-w-[45%] min-w-0 items-center gap-2"
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
          >
            {onExit ? (
              <button
                aria-label={exitLabel ?? 'Back'}
                className="flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-pill bg-surface px-3.5 text-ui font-medium whitespace-nowrap text-text-primary shadow-popover ring-1 ring-hairline transition hover:bg-surface-hover active:scale-[0.98]"
                onClick={onExit}
                type="button"
              >
                <ArrowLeftIcon className="size-4 shrink-0" />
                <span className="hidden lg:inline">{exitLabel ?? 'Back'}</span>
              </button>
            ) : null}
            <div className="flex h-10 min-w-0 items-center gap-2.5 rounded-pill bg-surface px-4 shadow-popover ring-1 ring-hairline">
              {titlePending ? (
                <Skeleton className="h-4 w-40 rounded-pill" />
              ) : (
                <h1 className="!m-0 !truncate !text-ui !font-semibold !text-text-primary duration-300 animate-in fade-in motion-reduce:animate-none">
                  {title}
                </h1>
              )}
              {/* A page's own word about the board (the walk's progress) —
                  never a count of the screens, which are right there. */}
              {status}
            </div>
          </div>

          {topRight ? (
            <div
              className="absolute top-3 z-20 flex items-center gap-2 transition-[right] duration-300 ease-out"
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
              style={{ right: chromeRight }}
            >
              {topRight}
            </div>
          ) : null}

          {notice ? (
            <div
              className="pointer-events-none absolute top-16 left-0 z-10 flex justify-center"
              style={{ width: clearWidth ?? '100%' }}
            >
              {notice}
            </div>
          ) : null}

          {/* Floating on the board, inset from its edges. Pointer-down stops
              here so pressing inside the panel never starts a pan. */}
          {panel ? (
            <div
              className={[
                'absolute top-3 right-3 bottom-3 flex cursor-auto select-text',
                profileOpen ? 'z-30' : 'z-10',
              ].join(' ')}
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
              ref={panelRef}
            >
              {panel}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * The bar over a screen on the board, like a Figma frame's name — chrome,
 * not content, so it never covers the screenshot. White, with the screen's
 * name and size; a spinner while the screen is still being captured. In
 * board pixels, so it scales with the screen under it.
 */
function ScreenBar({
  busy = false,
  label,
  meta,
}: {
  busy?: boolean;
  label: string;
  meta: string;
}) {
  return (
    <div
      className="absolute right-0 bottom-full left-0"
      style={{ height: LABEL_HEIGHT, paddingBottom: LABEL_GAP }}
    >
      <div className="flex h-full items-center gap-4 rounded-card bg-surface px-7 shadow-bar">
        {busy ? (
          <LoaderCircle
            aria-hidden="true"
            className="size-7 shrink-0 animate-spin text-room-violet motion-reduce:animate-none"
          />
        ) : null}
        <span className="min-w-0 truncate font-heading text-page-title font-bold text-text-primary">
          {label}
        </span>
        <span className="shrink-0 text-panel-title text-text-muted">
          {meta}
        </span>
      </div>
    </div>
  );
}

/**
 * A capture's picture that never blanks when its URL changes.
 *
 * Read URLs are signed, so every refetch of the board — the name poll after a
 * capture lands, for one — hands back a new URL for the same image. Swapping
 * `src` straight away blanked the card while the new one loaded: a flash on a
 * screen that had not changed. The new URL is shown once it has decoded.
 */
function CaptureImage({
  src,
  ...props
}: React.ImgHTMLAttributes<HTMLImageElement> & { src: string }) {
  const [shown, setShown] = useState(src);
  // The first picture fades in over its own shape once it has loaded,
  // rather than painting in piece by piece over nothing.
  const [loaded, setLoaded] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    // Already in the cache: it may have finished before React listened.
    if (imageRef.current?.complete) setLoaded(true);
  }, []);
  useEffect(() => {
    if (src === shown) return;
    let live = true;
    const show = () => {
      if (live) setShown(src);
    };
    const next = new Image();
    next.src = src;
    if (typeof next.decode === 'function') next.decode().then(show, show);
    else show();
    return () => {
      live = false;
    };
  }, [shown, src]);
  return (
    <>
      {loaded ? null : <Skeleton className="absolute inset-0 rounded-thumb" />}
      <img
        {...props}
        alt={props.alt}
        onLoad={() => {
          setLoaded(true);
        }}
        ref={imageRef}
        src={shown}
        style={{
          opacity: loaded ? 1 : 0,
          transition: 'opacity 400ms ease-out, box-shadow 500ms ease-out',
        }}
      />
    </>
  );
}
